import React, { useEffect, useState } from 'react';
import { api } from '../api';

const PRESETS = [
  { id: 'all',     label: 'All time',       days: null },
  { id: '30d',     label: 'Last 30 days',   days: 30 },
  { id: '90d',     label: 'Last 90 days',   days: 90 },
  { id: '180d',    label: 'Last 6 months',  days: 180 },
  { id: '365d',    label: 'Last 12 months', days: 365 },
];

// Format a dollar amount compactly. $1,234,000 -> "$1.2M", $12,300 -> "$12k".
function fmtMoney(n) {
  const v = Number(n || 0);
  if (!v) return '$0';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
  if (v >= 1_000)     return `$${Math.round(v / 1_000)}k`;
  return `$${v.toFixed(0)}`;
}

// Pick a teal/amber/coral palette for a conversion bar based on health.
// Mirrors the legend at the bottom of the table.
function convBarColor(pct) {
  if (pct == null) return { fill: '#1f2937', text: '#94a3b8' };
  if (pct >= 50) return { fill: '#0F6E56', text: '#9FE1CB' }; // teal
  if (pct >= 25) return { fill: '#854F0B', text: '#FAC775' }; // amber
  return { fill: '#791F1F', text: '#F09595' };                // coral
}

// ============================================================================
// Activity summary — "what happened this week" + per-week bar chart.
// Lives above the deal funnel on the reporting page. Data comes from
// /api/reporting/activity-summary which returns totals, this-week scalars,
// and a per-week series for outreach starts / demos / new prospects /
// bucket transitions (with a bucket dropdown for the last one).
// ============================================================================
function ActivitySummary() {
  const [weeks, setWeeks]   = useState(12);
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(false);
  // Active series shown in the bar chart. 'bucket' is special — it also
  // requires a bucket filter to be set (otherwise the dropdown's default).
  const [metric, setMetric] = useState('outreach');
  const [bucketId, setBucketId] = useState('');
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    setLoad(true);
    setLoadError(null);
    api.getActivitySummary({ weeks })
      .then((d) => { setData(d); setLoadError(null); })
      .catch((e) => { setData(null); setLoadError(e.message || 'Failed to load activity'); })
      .finally(() => setLoad(false));
  }, [weeks]);

  // Default the bucket dropdown to whichever bucket has the most changes
  // this week, so the chart isn't empty on first paint.
  useEffect(() => {
    if (!data || bucketId) return;
    const changes = data.this_week?.bucket_changes || {};
    const top = Object.entries(changes).sort((a, b) => b[1] - a[1])[0];
    if (top) setBucketId(top[0]);
    else if (data.bucket_definitions?.length) setBucketId(data.bucket_definitions[0].id);
  }, [data, bucketId]);

  const totals     = data?.totals || {};
  const thisWeek   = data?.this_week || {};
  const bucketDefs = data?.bucket_definitions || [];

  // Derive the bar-chart series from the selected metric.
  let series = [];
  let seriesLabel = '';
  let seriesColor = '#7F77DD'; // purple default
  if (data) {
    if (metric === 'outreach') {
      series = data.weekly.outreach || [];
      seriesLabel = 'Outreach touches logged (notes + follow-ups)';
      seriesColor = '#7F77DD';
    } else if (metric === 'demos') {
      series = data.weekly.demos || [];
      seriesLabel = 'Outreach → Demo conversions';
      seriesColor = '#1D9E75';
    } else if (metric === 'new_companies') {
      series = data.weekly.new_companies || [];
      seriesLabel = 'New prospects added';
      seriesColor = '#185FA5';
    } else if (metric === 'bucket') {
      const rows = (data.weekly.bucket || []).filter(r => r.bucket === bucketId);
      // Re-shape to {week, n}.
      series = rows.map(r => ({ week: r.week, n: r.n }));
      const def = bucketDefs.find(b => b.id === bucketId);
      seriesLabel = `Companies moved to "${def?.label || bucketId || 'bucket'}"`;
      seriesColor = def?.color || '#D85A30';
    }
  }

  // Disqualified bucket: try to find a bucket whose id/label hints at "lost"
  // or "unqualified" so the stat card has a sensible default.
  const disqualifiedBucketId = (() => {
    for (const b of bucketDefs) {
      const hay = `${b.id} ${b.label}`.toLowerCase();
      if (/disqualif|unqualif|junk|reject|lost/.test(hay)) return b.id;
    }
    return null;
  })();
  const disqualifiedThisWeek = disqualifiedBucketId
    ? (thisWeek.bucket_changes?.[disqualifiedBucketId] || 0)
    : 0;

  const maxN = Math.max(1, ...series.map(s => s.n || 0));

  return (
    <div className="mb-8">
      <div className="flex items-baseline justify-between mb-1">
        <h1 className="text-xl font-semibold text-white">Activity</h1>
        <div className="flex items-center gap-1">
          {[4, 8, 12, 26].map(w => (
            <button key={w}
              onClick={() => setWeeks(w)}
              className={`px-2.5 py-1 text-xs rounded-full border ${
                weeks === w
                  ? 'bg-indigo-600/20 border-indigo-500 text-indigo-200'
                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'
              }`}
            >{w}w</button>
          ))}
        </div>
      </div>
      <div className="text-slate-500 text-xs mb-4">
        This week's headline numbers, then a per-week breakdown over the last {weeks} weeks.
      </div>

      {loadError && !data ? (
        <div className="text-red-300 text-sm py-6 bg-red-900/20 border border-red-700/50 rounded px-3">
          Failed to load activity: {loadError}
        </div>
      ) : loading && !data ? (
        <div className="text-slate-500 text-sm py-6">Loading activity…</div>
      ) : (
        <>
          {/* This-week stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
            <StatCard label="Outreach this wk"      value={thisWeek.outreach_starts} accent="#7F77DD" sub="notes + follow-ups logged" />
            <StatCard label="Demos this wk"         value={thisWeek.demos}           accent="#1D9E75" sub="outreach → demo" />
            <StatCard label="Disqualified this wk"
              value={disqualifiedThisWeek}
              accent="#D85A30"
              sub={disqualifiedBucketId
                ? `→ ${bucketDefs.find(b => b.id === disqualifiedBucketId)?.label || disqualifiedBucketId}`
                : 'no disqualified bucket set'}
            />
            <StatCard label="New prospects wk"      value={thisWeek.new_companies}   accent="#185FA5" sub="companies created" />
            <StatCard label="Total in Siphon"
              value={totals.companies}
              accent="#94a3b8"
              sub={`${totals.leads || 0} leads · ${totals.deals || 0} deals`}
            />
          </div>

          {/* Metric tabs + (optional) bucket selector */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <MetricTab id="outreach"      active={metric} onClick={setMetric} label="Outreach" />
            <MetricTab id="demos"         active={metric} onClick={setMetric} label="Outreach → Demo" />
            <MetricTab id="new_companies" active={metric} onClick={setMetric} label="New prospects" />
            <MetricTab id="bucket"        active={metric} onClick={setMetric} label="Bucket transitions" />
            {metric === 'bucket' && (
              <select
                value={bucketId}
                onChange={(e) => setBucketId(e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 ml-1"
              >
                {bucketDefs.length === 0 && <option value="">No buckets defined</option>}
                {bucketDefs.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.emoji ? `${b.emoji} ` : ''}{b.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Bar chart — inline SVG, last `weeks` weekly buckets */}
          <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
              {seriesLabel}
            </div>
            {series.length === 0 ? (
              <div className="text-slate-600 text-xs py-6 text-center italic">
                No data in the last {weeks} weeks for this metric.
              </div>
            ) : (
              <WeeklyBars data={series} weeks={weeks} color={seriesColor} maxN={maxN} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, accent = '#94a3b8' }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-2xl font-semibold mt-0.5" style={{ color: accent }}>
        {value ?? 0}
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5 truncate" title={sub}>{sub}</div>
    </div>
  );
}

function MetricTab({ id, active, onClick, label }) {
  const on = id === active;
  return (
    <button
      onClick={() => onClick(id)}
      className={`px-3 py-1.5 text-xs rounded border ${
        on
          ? 'bg-indigo-600/20 border-indigo-500 text-indigo-200'
          : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'
      }`}
    >{label}</button>
  );
}

// Inline SVG bar chart. Pads the series with zero-weeks so empty leading
// weeks still show as gaps instead of compressing the x-axis.
function WeeklyBars({ data, weeks, color, maxN }) {
  // Build a continuous Sunday-truncated week axis ending on the most-recent
  // week, of length `weeks`. Then merge in the data points.
  const today = new Date();
  const oneWeek = 7 * 24 * 60 * 60 * 1000;
  const dayOfWeek = today.getDay(); // 0=Sun
  const thisWeekStart = new Date(today);
  thisWeekStart.setHours(0, 0, 0, 0);
  thisWeekStart.setDate(thisWeekStart.getDate() - dayOfWeek + 1); // Monday-start (Postgres date_trunc('week') is Mon)
  const axis = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const d = new Date(thisWeekStart.getTime() - i * oneWeek);
    axis.push({ key: d.toISOString().slice(0, 10), date: d });
  }
  const byKey = new Map(data.map(d => [d.week, d.n]));
  const points = axis.map(a => ({ ...a, n: byKey.get(a.key) || 0 }));

  const W = 700, H = 160, padT = 10, padB = 24, padL = 28, padR = 8;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const barGap = 4;
  const barW = (innerW - barGap * (points.length - 1)) / points.length;

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMinYMid meet"
           style={{ display: 'block' }}>
        {/* horizontal axis */}
        <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#334155" strokeWidth="1"/>
        {/* y-axis ticks (0, mid, max) */}
        {[0, Math.ceil(maxN / 2), maxN].map((v, i) => {
          const y = H - padB - (v / maxN) * innerH;
          return (
            <g key={i}>
              <line x1={padL - 4} y1={y} x2={padL} y2={y} stroke="#334155"/>
              <text x={padL - 6} y={y + 3} fontSize="10" fill="#64748b" textAnchor="end">{v}</text>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#1e293b" strokeDasharray="2 4"/>
            </g>
          );
        })}
        {/* bars */}
        {points.map((p, idx) => {
          const x = padL + idx * (barW + barGap);
          const h = (p.n / maxN) * innerH;
          const y = H - padB - h;
          const showLabel = (idx === points.length - 1 || idx % Math.max(1, Math.ceil(points.length / 8)) === 0);
          const monthDay = `${p.date.getMonth() + 1}/${p.date.getDate()}`;
          return (
            <g key={p.key}>
              <rect x={x} y={y} width={Math.max(2, barW)} height={Math.max(0, h)}
                    fill={color} rx="2">
                <title>{p.key} · {p.n}</title>
              </rect>
              {p.n > 0 && (
                <text x={x + barW / 2} y={y - 3} fontSize="9.5" fill="#cbd5e1" textAnchor="middle">{p.n}</text>
              )}
              {showLabel && (
                <text x={x + barW / 2} y={H - padB + 14} fontSize="9.5" fill="#64748b" textAnchor="middle">{monthDay}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default function ReportingPanel() {
  const [preset, setPreset] = useState('all');
  const [customSince, setCustomSince] = useState('');
  const [customUntil, setCustomUntil] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [preset, customSince, customUntil]);

  async function load() {
    setLoading(true); setError('');
    try {
      let since = null, until = null;
      if (preset === 'custom') {
        since = customSince || null;
        until = customUntil || null;
      } else if (preset !== 'all') {
        const p = PRESETS.find((x) => x.id === preset);
        if (p?.days) {
          const d = new Date();
          d.setDate(d.getDate() - p.days);
          since = d.toISOString().slice(0, 10);
        }
      }
      const result = await api.getDealFunnel({ since, until });
      setData(result);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  const stages = data?.stages || [];

  // Total pipeline + deal count for the snapshot header line.
  const pipelineTotal = stages.reduce((s, r) => s + (r.current_value || 0), 0);
  const pipelineDeals = stages.reduce((s, r) => s + (r.current_count || 0), 0);

  // Find the leakiest hop (lowest conversion from prev) so the footer can
  // call it out. Skip the first stage (no prev) and stages with no sample.
  const leakiest = stages.reduce((worst, s, idx) => {
    if (idx === 0 || s.from_prev_pct == null || (s.prev_window_count || 0) === 0) return worst;
    if (!worst || s.from_prev_pct < worst.from_prev_pct) return s;
    return worst;
  }, null);

  // Window label for the conversion column header.
  const presetLabel = PRESETS.find((p) => p.id === preset)?.label;
  const windowLabel = preset === 'all' ? 'all-time'
                    : preset === 'custom'
                      ? `${customSince || '…'} → ${customUntil || 'now'}`
                      : (presetLabel || preset);

  return (
    // h-full + overflow-y-auto so the panel scrolls inside the app shell.
    // Inner wrapper holds the max-w-4xl constraint so content stays readable
    // on wide screens while the scroll container fills the column.
    <div className="h-full overflow-y-auto scrollable-content">
      <div className="p-8 max-w-4xl">
      <ActivitySummary />

      <h1 className="text-xl font-semibold text-white mb-1">Deal funnel</h1>
      <div className="text-slate-500 text-xs mb-6">
        Current pipeline on the left, historical conversion rates on the right.
        Stage moves flagged as accidental are excluded from conversion math.
      </div>

      {/* Date filters */}
      <div className="flex items-center flex-wrap gap-2 mb-6">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPreset(p.id)}
            className={`px-3 py-1.5 text-xs rounded-full border ${
              preset === p.id
                ? 'bg-indigo-600/20 border-indigo-500 text-indigo-200'
                : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'
            }`}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={() => setPreset('custom')}
          className={`px-3 py-1.5 text-xs rounded-full border ${
            preset === 'custom'
              ? 'bg-indigo-600/20 border-indigo-500 text-indigo-200'
              : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'
          }`}
        >
          Custom…
        </button>
        {preset === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input
              type="date" value={customSince} onChange={(e) => setCustomSince(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs"
            />
            <span className="text-slate-500 text-xs">→</span>
            <input
              type="date" value={customUntil} onChange={(e) => setCustomUntil(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs"
            />
          </div>
        )}
      </div>

      {error && <div className="text-red-400 text-sm mb-4">{error}</div>}
      {loading && <div className="text-slate-500 text-sm mb-4">Loading…</div>}

      {/* Snapshot header: total deals + total $ in pipeline right now. */}
      {data && (
        <div className="text-xs text-slate-400 mb-3">
          <span className="text-slate-200 font-medium">{pipelineDeals}</span> deals in pipeline ·
          <span className="text-slate-200 font-medium ml-1">{fmtMoney(pipelineTotal)}</span>
          <span className="text-slate-600 ml-2">· conversion rates over {windowLabel}</span>
        </div>
      )}

      {/* Stage table — current snapshot on the left, historical conversion on the right. */}
      <div className="bg-slate-800/30 border border-slate-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '22%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '46%' }} />
          </colgroup>
          <thead className="bg-slate-900/60 text-slate-400 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left py-2 px-3 font-medium">Stage</th>
              <th className="text-right py-2 px-3 font-medium">Deals</th>
              <th className="text-right py-2 px-3 font-medium">Value</th>
              <th className="text-left py-2 px-3 font-medium">Convert to next stage</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((s, idx) => {
              const isLast = idx === stages.length - 1 || s.is_won;
              const pct = s.from_prev_pct;
              const barColor = convBarColor(pct);
              const advanced = s.advanced_count ?? 0;
              const denom = s.prev_window_count ?? 0;
              const dropped = Math.max(0, denom - advanced);
              return (
                <tr key={s.stage} className="border-t border-slate-700/60">
                  <td className="py-3 px-3 text-slate-100">{s.stage_label || s.stage}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-slate-100">{s.current_count || 0}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-slate-100">{fmtMoney(s.current_value)}</td>
                  <td className="py-3 px-3">
                    {isLast || !s.next_stage ? (
                      <span className="text-[11px] text-slate-500">End of funnel</span>
                    ) : (
                      <>
                        <div className="flex items-center gap-2.5">
                          <div className="relative flex-1 h-6 bg-slate-900 rounded overflow-hidden">
                            <div
                              className="absolute left-0 top-0 bottom-0 rounded transition-all"
                              style={{
                                width: pct != null ? `${Math.max(2, Math.min(100, pct))}%` : '0%',
                                backgroundColor: barColor.fill,
                              }}
                            />
                            <div className="absolute inset-0 flex items-center px-2.5 text-[13px] font-medium" style={{ color: pct == null ? '#94a3b8' : '#fff' }}>
                              {pct != null ? `${Math.round(pct)}%` : '—'}
                            </div>
                          </div>
                          <span className="text-[11px] text-slate-400 whitespace-nowrap">→ {s.next_stage_label || s.next_stage}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1">
                          {denom > 0
                            ? `${advanced} of ${denom} in window advanced · ${dropped} dropped`
                            : 'No deals hit this stage in the selected window'}
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend + leakiest-stage callout */}
      <div className="mt-3 flex items-center gap-5 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#0F6E56' }} /> ≥ 50%</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#854F0B' }} /> 25–49%</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#791F1F' }} /> &lt; 25%</span>
        {leakiest && (
          <span className="ml-auto text-amber-400">
            Leakiest hop: {stages[stages.findIndex(s => s.stage === leakiest.stage) - 1]?.stage_label} → {leakiest.stage_label} ({Math.round(leakiest.from_prev_pct)}%)
          </span>
        )}
      </div>

      {data && stages.every((s) => (s.current_count || 0) === 0 && (s.reached_count || 0) === 0) && (
        <div className="mt-8 text-center text-slate-500 text-sm">
          No deals in the pipeline yet. Create or import deals on the Deals page and they'll show up here.
        </div>
      )}

      {/* Lost-by-stage breakdown — shows where deals tend to die. */}
      {data?.lost_by_stage?.length > 0 && (
        <div className="mt-10">
          <h2 className="text-sm font-semibold text-slate-200 mb-3 uppercase tracking-wide">
            Where deals are getting lost
          </h2>
          <div className="text-xs text-slate-500 mb-3">
            The stage each lost deal was in immediately before closed_lost.
          </div>
          <div className="bg-slate-800/40 border border-slate-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left py-2 px-3">Lost from stage</th>
                  <th className="text-right py-2 px-3">Deals lost</th>
                </tr>
              </thead>
              <tbody>
                {data.lost_by_stage.map((row) => (
                  <tr key={row.from_stage} className="border-t border-slate-700">
                    <td className="py-2 px-3 text-slate-200">{row.from_stage}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-red-400">{row.deals_lost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
